import { useState, useEffect, useCallback, useRef } from "react";
import { useDialogFocus } from "../../../../hooks/useDialogFocus";
import { Button } from "../../../../components/Button";
import { ModalCloseButton } from "../../../../components/ModalCloseButton";
import {
  TeamTypeahead,
  type TeamOption,
} from "../../../../components/TeamTypeahead";
import {
  bulkAddProspectsToTeam,
  createTeam,
} from "../../../../services/teams";
import { getSafeErrorMessage } from "../../../../utils/errorHandler";
import type { BulkAddProspectsToTeamResponse } from "../../../../types/api";
import "./AddPlayersToTeamModal.css";

interface AddPlayersToTeamModalProps {
  isOpen: boolean;
  prospectIds: string[];
  onClose: () => void;
  onSuccess: (
    result: BulkAddProspectsToTeamResponse,
    teamCreated: boolean
  ) => void;
}

type Step = "pick" | "summary";

export const AddPlayersToTeamModal = ({
  isOpen,
  prospectIds,
  onClose,
  onSuccess,
}: AddPlayersToTeamModalProps) => {
  const [step, setStep] = useState<Step>("pick");
  const [selectedTeam, setSelectedTeam] = useState<TeamOption | null>(null);
  // Preserved across retries so a successful createTeam + failed bulk-add doesn't
  // create duplicate teams on the next submit.
  const [resolvedTeamId, setResolvedTeamId] = useState<string | null>(null);
  const [teamCreated, setTeamCreated] = useState(false);
  const [result, setResult] = useState<BulkAddProspectsToTeamResponse | null>(
    null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Reset state when the modal opens.
  useEffect(() => {
    if (isOpen) {
      setStep("pick");
      setSelectedTeam(null);
      setResolvedTeamId(null);
      setTeamCreated(false);
      setResult(null);
      setIsSubmitting(false);
      setError("");
    }
  }, [isOpen]);

  // ESC to close — disabled while submitting so we don't orphan an in-flight request.
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isSubmitting) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen, isSubmitting, onClose]);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget && !isSubmitting) {
        onClose();
      }
    },
    [isSubmitting, onClose]
  );

  const trimmedName = selectedTeam?.name.trim() ?? "";
  const canSubmit =
    !isSubmitting &&
    selectedTeam !== null &&
    (selectedTeam.id !== null || trimmedName.length > 0);

  const handleSubmit = useCallback(async () => {
    if (!selectedTeam) {
      setError("Please select a team");
      return;
    }
    if (selectedTeam.id === null && trimmedName.length === 0) {
      setError("Team name cannot be empty");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      let teamId = resolvedTeamId ?? selectedTeam.id;
      let didCreateTeam = teamCreated;

      // Step 1: resolve a real team id (create if the user typed a new name)
      if (!teamId) {
        const created = await createTeam(trimmedName);
        teamId = created.id;
        didCreateTeam = created.isNew;
        setResolvedTeamId(teamId);
        setTeamCreated(didCreateTeam);
      }

      // Step 2: bulk-add
      const response = await bulkAddProspectsToTeam(teamId, prospectIds);
      setResult(response);
      setStep("summary");
      // Surface the outcome to the parent so it can refresh prospects/teams.
      onSuccess(response, didCreateTeam);
    } catch (err) {
      setError(getSafeErrorMessage(err, "Failed to add players to team"));
    } finally {
      setIsSubmitting(false);
    }
  }, [
    selectedTeam,
    trimmedName,
    resolvedTeamId,
    teamCreated,
    prospectIds,
    onSuccess,
  ]);

  const dialogRef = useRef<HTMLDivElement>(null);

  useDialogFocus({ isOpen, dialogRef, handleEscape: false });

  if (!isOpen) return null;

  const attemptedCount = prospectIds.length;

  return (
    <div
      className="add-players-to-team-modal__overlay"
      onClick={handleOverlayClick}
    >
      <div ref={dialogRef} tabIndex={-1}
        className="add-players-to-team-modal__content"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-players-to-team-title"
      >
        <div className="add-players-to-team-modal__header">
          <div className="add-players-to-team-modal__header-content">
            <h3
              id="add-players-to-team-title"
              className="add-players-to-team-modal__title"
            >
              {step === "pick" ? "Add Players to Team" : "Done"}
            </h3>
            <p className="add-players-to-team-modal__subtitle">
              {step === "pick"
                ? `${attemptedCount} player${attemptedCount !== 1 ? "s" : ""} selected`
                : result?.teamName}
            </p>
          </div>
          <ModalCloseButton onClick={onClose} disabled={isSubmitting} />
        </div>

        {step === "pick" ? (
          <>
            <div className="add-players-to-team-modal__body">
              <label htmlFor="add-players-to-team-modal-search-for-existing-team-or-create-0" className="add-players-to-team-modal__label">
                Search for existing team or create new
              </label>
              <TeamTypeahead inputId="add-players-to-team-modal-search-for-existing-team-or-create-0"
                value={selectedTeam}
                onSelect={(team) => {
                  setSelectedTeam(team);
                  // If the user changes their mind about the team, forget the previously
                  // resolved id so we don't stick a brand-new team to an unrelated submit.
                  setResolvedTeamId(null);
                  setTeamCreated(false);
                  setError("");
                }}
                placeholder="Start typing team name..."
              />
              {error && (
                <div className="add-players-to-team-modal__error" role="alert">
                  {error}
                </div>
              )}
            </div>

            <div className="add-players-to-team-modal__footer">
              <Button
                variant="secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={!canSubmit}
              >
                {isSubmitting
                  ? "Adding..."
                  : `Add ${attemptedCount} Player${attemptedCount !== 1 ? "s" : ""}`}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="add-players-to-team-modal__body">
              <SummaryView result={result} teamCreated={teamCreated} />
            </div>
            <div className="add-players-to-team-modal__footer">
              <Button variant="primary" onClick={onClose} fullWidth>
                Done
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

interface SummaryViewProps {
  result: BulkAddProspectsToTeamResponse | null;
  teamCreated: boolean;
}

const SummaryView = ({ result, teamCreated }: SummaryViewProps) => {
  if (!result) return null;

  const {
    addedCount,
    alreadyOnTeamProspectIds,
    notFoundProspectIds,
    teamName,
  } = result;
  const alreadyOnCount = alreadyOnTeamProspectIds.length;
  const notFoundCount = notFoundProspectIds.length;

  // Lead with the headline result; the "all already on team" case needs its own copy.
  const headline =
    addedCount === 0 && alreadyOnCount > 0
      ? `No new players were added — all selected were already on ${teamName}.`
      : `Added ${addedCount} player${addedCount !== 1 ? "s" : ""} to ${teamName}${
          teamCreated ? " (new team)" : ""
        }.`;

  return (
    <div className="add-players-to-team-modal__summary">
      <p className="add-players-to-team-modal__summary-headline">{headline}</p>
      {alreadyOnCount > 0 && addedCount > 0 && (
        <p className="add-players-to-team-modal__summary-note">
          {alreadyOnCount} {alreadyOnCount === 1 ? "was" : "were"} already on
          this team.
        </p>
      )}
      {notFoundCount > 0 && (
        <p className="add-players-to-team-modal__summary-note">
          {notFoundCount}{" "}
          {notFoundCount === 1 ? "player was" : "players were"} no longer
          available and {notFoundCount === 1 ? "was" : "were"} skipped.
        </p>
      )}
    </div>
  );
};
